import { css } from '@emotion/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import INodeAgent from 'types/INodeAgent';
import deleteNodeAgent from 'store/deleteNodeAgent';
import deleteWorker from 'store/deleteWorker';
import sendNodeCommand from 'store/sendNodeCommand';
import useProjectsAsync from 'store/useProjectsAsync';
import CleanupWorkerDialog from './CleanupWorkerDialog';
import ProjectFilterCell from './ProjectFilterCell';
import RetireWorkerDialog from './RetireWorkerDialog';
import ResizeWorkerDialog from './ResizeWorkerDialog';
import EvictWorkerTasksDialog from './EvictWorkerTasksDialog';
import StopWorkerDialog from './StopWorkerDialog';
import UpgradeWorkerDialog from './UpgradeWorkerDialog';
import WorkerActionsMenu from './WorkerActionsMenu';

interface NodeTableProps {
  nodes: INodeAgent[];
  onStartWorker: (nodeId: string) => void;
  provisioningNodeId: string | null;
  onMutate: () => void;
}

function formatLastSeen(lastSeen: string): string {
  if (!lastSeen) return 'Never';
  const date = new Date(lastSeen);
  if (isNaN(date.getTime())) return 'Invalid';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const time = date.toLocaleTimeString(undefined, { hour12: false });
  return `${y}-${m}-${d} ${time}`;
}

export default function NodeTable({ nodes, onStartWorker, provisioningNodeId, onMutate }: NodeTableProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [cleanupTarget, setCleanupTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [retireTarget, setRetireTarget] = useState<{
    workerId: string;
    workerName: string;
    packageName?: string;
    packageVersion?: string;
    trackingKey: string;
  } | null>(null);
  const [retiringWorkers, setRetiringWorkers] = useState<Set<string>>(new Set());
  const retiringTimestamps = useRef<Map<string, number>>(new Map());
  const [cleaningUpWorkers, setCleaningUpWorkers] = useState<Set<string>>(new Set());
  const [stoppingWorkers, setStoppingWorkers] = useState<Set<string>>(new Set());
  const [stopTarget, setStopTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [removingWorkers, setRemovingWorkers] = useState<Set<string>>(new Set());
  const [deletingWorkers, setDeletingWorkers] = useState<Set<string>>(new Set());
  const [removingNodes, setRemovingNodes] = useState<Set<string>>(new Set());
  const [restartingWorkers, setRestartingWorkers] = useState<Set<string>>(new Set());
  const [upgradingWorkers, setUpgradingWorkers] = useState<Map<string, string | undefined>>(new Map());
  const [upgradeTarget, setUpgradeTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
    currentPackageName: string;
    currentVersion?: string;
    workdirName?: string;
  } | null>(null);
  const [resizingWorkers, setResizingWorkers] = useState<Map<string, number>>(new Map());
  const [resizeTarget, setResizeTarget] = useState<{
    workerId: string;
    workerName: string;
    currentCores: number;
  } | null>(null);
  const [evictingWorkers, setEvictingWorkers] = useState<Set<string>>(new Set());
  const [evictTarget, setEvictTarget] = useState<{
    workerId: string;
    workerName: string;
    evictCount: number;
    isRetiring: boolean;
  } | null>(null);

  const projectsAsync = useProjectsAsync();
  const projectNamesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projectsAsync.data ?? []) map.set(project.id, project.name);
    return map;
  }, [projectsAsync.data]);
  const projectsLoaded = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH';

  // Auto-expand the node that is being provisioned. Adjusting state during
  // render per the React docs' previous-prop pattern — only fires on the
  // transition, leaving manual collapses by the user intact.
  const [prevProvisioningNodeId, setPrevProvisioningNodeId] = useState(provisioningNodeId);
  if (prevProvisioningNodeId !== provisioningNodeId) {
    setPrevProvisioningNodeId(provisioningNodeId);
    if (provisioningNodeId && !expandedNodes.has(provisioningNodeId)) {
      setExpandedNodes((prev) => new Set(prev).add(provisioningNodeId));
    }
  }

  // Clear upgradingWorkers entries once the upgrade completes.
  // We track the installed_version at the time the upgrade was initiated;
  // once the version changes (or the status becomes UPGRADE_FAILED), we
  // know the upgrade finished and can clear the local flag. Same render-time
  // previous-prop pattern, keyed on the nodes snapshot from the server.
  const [prevNodesForUpgrade, setPrevNodesForUpgrade] = useState(nodes);
  if (prevNodesForUpgrade !== nodes) {
    setPrevNodesForUpgrade(nodes);
    if (upgradingWorkers.size > 0) {
      const confirmed: string[] = [];
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (!ws.instance_id) continue;
          const key = `${node.id}:${ws.instance_id}`;
          if (!upgradingWorkers.has(key)) continue;
          const oldVersion = upgradingWorkers.get(key);
          const currentVersion = ws.installed_version || ws.package_version;
          if (ws.status === 'UPGRADE_FAILED' || (oldVersion && currentVersion && currentVersion !== oldVersion)) {
            confirmed.push(key);
          }
        }
      }
      if (confirmed.length > 0) {
        setUpgradingWorkers((prev) => {
          const next = new Map(prev);
          for (const key of confirmed) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback: clear all upgrading entries after 30s.
  // Handles the case where upgrade completes between data refreshes
  // (frontend never sees UPGRADING status).
  useEffect(() => {
    if (upgradingWorkers.size === 0) return;
    const timer = setTimeout(() => setUpgradingWorkers(new Map()), 30000);
    return () => clearTimeout(timer);
  }, [upgradingWorkers.size]);

  // Clear resizingWorkers once the server reflects the requested core count.
  // Keyed by worker_id (the PATCH target); the backend reports ncores from the
  // same WorkerDao.cores the PATCH set, so it matches on the next refresh. Same
  // render-time previous-prop pattern as the upgrade reconcile above.
  const [prevNodesForResize, setPrevNodesForResize] = useState(nodes);
  if (prevNodesForResize !== nodes) {
    setPrevNodesForResize(nodes);
    if (resizingWorkers.size > 0) {
      const confirmed: string[] = [];
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (!ws.worker_id || !resizingWorkers.has(ws.worker_id)) continue;
          if (ws.ncores === resizingWorkers.get(ws.worker_id)) confirmed.push(ws.worker_id);
        }
      }
      if (confirmed.length > 0) {
        setResizingWorkers((prev) => {
          const next = new Map(prev);
          for (const key of confirmed) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback: clear all resizing entries after 30s, in case the reported ncores
  // never lands exactly on the request (e.g. a concurrent restart).
  useEffect(() => {
    if (resizingWorkers.size === 0) return;
    const timer = setTimeout(() => setResizingWorkers(new Map()), 30000);
    return () => clearTimeout(timer);
  }, [resizingWorkers]);

  // Reconcile retiringWorkers: clear when server reports status !== RUNNING or worker disappears.
  // Only runs when server data changes (nodes). A 10s grace period prevents clearing
  // entries that were just added (server hasn't caught up yet).
  useEffect(() => {
    const GRACE_MS = 10_000;
    const now = Date.now();
    setRetiringWorkers((prev) => {
      if (prev.size === 0) return prev;
      const allWorkerKeys = new Set<string>();
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (ws.instance_id) allWorkerKeys.add(`${node.id}:${ws.instance_id}`);
          if (ws.worker_id) allWorkerKeys.add(ws.worker_id);
        }
      }
      const toClear: string[] = [];
      for (const key of prev) {
        if (now - (retiringTimestamps.current.get(key) ?? 0) < GRACE_MS) continue;
        if (!allWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : ws.worker_id;
            if (wsKey === key && ws.status !== 'RUNNING') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length === 0) return prev;
      const next = new Set(prev);
      for (const key of toClear) {
        next.delete(key);
        retiringTimestamps.current.delete(key);
      }
      return next;
    });
  }, [nodes]);

  // Fallback timeout for retiringWorkers: 60s (graceful shutdown takes time)
  useEffect(() => {
    if (retiringWorkers.size === 0) return;
    const timer = setTimeout(() => {
      retiringTimestamps.current.clear();
      setRetiringWorkers(new Set());
    }, 60000);
    return () => clearTimeout(timer);
  }, [retiringWorkers.size]);

  // Fallback timeout for evictingWorkers: the kill confirms within a couple of
  // checkins, after which the row's running count drops on the next poll.
  useEffect(() => {
    if (evictingWorkers.size === 0) return;
    const timer = setTimeout(() => setEvictingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [evictingWorkers.size]);

  // Reconcile cleaningUpWorkers, removingWorkers, deletingWorkers, removingNodes:
  // clear when items disappear from server data. Same render-time
  // previous-prop pattern, keyed on the nodes snapshot.
  const [prevNodesForReconcile, setPrevNodesForReconcile] = useState(nodes);
  if (prevNodesForReconcile !== nodes) {
    setPrevNodesForReconcile(nodes);
    const currentNodeIds = new Set(nodes.map((n) => n.id));
    const currentWorkerKeys = new Set<string>();
    const currentWorkerIds = new Set<string>();
    for (const node of nodes) {
      for (const ws of node.worker_summaries || []) {
        if (ws.instance_id) currentWorkerKeys.add(`${node.id}:${ws.instance_id}`);
        if (ws.worker_id) currentWorkerIds.add(ws.worker_id);
      }
    }

    if (cleaningUpWorkers.size > 0) {
      const toClear = [...cleaningUpWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setCleaningUpWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingWorkers.size > 0) {
      const toClear = [...removingWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setRemovingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (stoppingWorkers.size > 0) {
      const toClear: string[] = [];
      for (const key of stoppingWorkers) {
        if (!currentWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : null;
            if (wsKey === key && ws.status === 'STOPPED') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length > 0) {
        setStoppingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (deletingWorkers.size > 0) {
      const toClear = [...deletingWorkers].filter((k) => !currentWorkerIds.has(k));
      if (toClear.length > 0) {
        setDeletingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (restartingWorkers.size > 0) {
      const toClear: string[] = [];
      for (const key of restartingWorkers) {
        if (!currentWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : null;
            if (wsKey === key && ws.status !== 'STOPPED') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length > 0) {
        setRestartingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingNodes.size > 0) {
      const toClear = [...removingNodes].filter((k) => !currentNodeIds.has(k));
      if (toClear.length > 0) {
        setRemovingNodes((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback timeout for cleaningUpWorkers, removingWorkers, deletingWorkers, removingNodes: 30s
  useEffect(() => {
    if (cleaningUpWorkers.size === 0) return;
    const timer = setTimeout(() => setCleaningUpWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [cleaningUpWorkers.size]);
  useEffect(() => {
    if (removingWorkers.size === 0) return;
    const timer = setTimeout(() => setRemovingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingWorkers.size]);
  useEffect(() => {
    if (stoppingWorkers.size === 0) return;
    const timer = setTimeout(() => setStoppingWorkers(new Set()), 60000);
    return () => clearTimeout(timer);
  }, [stoppingWorkers.size]);
  useEffect(() => {
    if (deletingWorkers.size === 0) return;
    const timer = setTimeout(() => setDeletingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [deletingWorkers.size]);
  useEffect(() => {
    if (removingNodes.size === 0) return;
    const timer = setTimeout(() => setRemovingNodes(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingNodes.size]);
  useEffect(() => {
    if (restartingWorkers.size === 0) return;
    const timer = setTimeout(() => setRestartingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [restartingWorkers.size]);

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  return (
    <Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 48 }} />
              <TableCell>Name</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Node ID</TableCell>
              <TableCell>State</TableCell>
              <TableCell>OS</TableCell>
              <TableCell>CPU</TableCell>
              <TableCell>Memory</TableCell>
              <TableCell>Disk</TableCell>
              <TableCell>Running Workers</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No nodes registered. Start a node agent with: <code>tailor node-agent</code>
                </TableCell>
              </TableRow>
            )}
            {[...nodes]
              .sort((a, b) => a.id.localeCompare(b.id))
              .map((node) => {
                const workers =
                  node.state === 'OFFLINE'
                    ? []
                    : [...(node.worker_summaries || [])].sort((a, b) => {
                        // Managed workers first
                        const aManaged = a.managed !== false ? 1 : 0;
                        const bManaged = b.managed !== false ? 1 : 0;
                        if (aManaged !== bManaged) return bManaged - aManaged;
                        return (
                          (a.name || '').localeCompare(b.name || '') ||
                          (a.instance_id || '').localeCompare(b.instance_id || '')
                        );
                      });
                const hasWorkers = workers.length > 0;
                const isProvisioning = provisioningNodeId === node.id;
                // Banners for in-flight actions on this node's workers.
                // The kebab menu auto-closes after click, so the menu-item
                // label "Retiring…"/"Cleaning up…" is gone before the action
                // completes — the banner is the persistent feedback. Mirrors
                // the "Starting new worker..." pattern used for provisioning.
                const inFlightBanners: { key: string; label: string }[] = [];
                for (const w of workers) {
                  const instanceKey = w.instance_id ? `${node.id}:${w.instance_id}` : null;
                  const workerId = w.worker_id || null;
                  const displayName = w.name || w.instance_id || w.worker_id || '?';
                  // Stable per-worker id so React keys never collide when two
                  // workers under the same node share a name (e.g. ASG-managed
                  // workers all named `w-ec2`, distinguished only by instance_id).
                  const bannerId = instanceKey || workerId || displayName;
                  const retiringHit =
                    (instanceKey && retiringWorkers.has(instanceKey)) || (workerId && retiringWorkers.has(workerId));
                  if (retiringHit) {
                    inFlightBanners.push({ key: `retire-${bannerId}`, label: `Retiring worker ${displayName}…` });
                  }
                  if (instanceKey && upgradingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `upgrade-${bannerId}`, label: `Upgrading worker ${displayName}…` });
                  }
                  if (instanceKey && stoppingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `stop-${bannerId}`, label: `Stopping worker ${displayName}…` });
                  }
                  if (instanceKey && cleaningUpWorkers.has(instanceKey)) {
                    inFlightBanners.push({
                      key: `cleanup-${bannerId}`,
                      label: `Cleaning up worker ${displayName}…`,
                    });
                  }
                  if (instanceKey && removingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `remove-${bannerId}`, label: `Removing worker ${displayName}…` });
                  }
                  if (workerId && deletingWorkers.has(workerId)) {
                    inFlightBanners.push({ key: `delete-${bannerId}`, label: `Removing worker ${displayName}…` });
                  }
                  if (instanceKey && restartingWorkers.has(instanceKey)) {
                    inFlightBanners.push({
                      key: `restart-${bannerId}`,
                      label: `Restarting worker ${displayName}…`,
                    });
                  }
                }
                const showWorkerSection = hasWorkers || isProvisioning || inFlightBanners.length > 0;
                const isExpanded = expandedNodes.has(node.id);

                return (
                  <Fragment key={node.id}>
                    <TableRow>
                      <TableCell>
                        {showWorkerSection && (
                          <IconButton size="small" onClick={() => toggleExpanded(node.id)}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{node.name || '-'}</TableCell>
                      <TableCell>{node.account_name || '-'}</TableCell>
                      <TableCell
                        css={css`
                          font-family: monospace;
                          font-size: 0.85rem;
                        `}
                      >
                        <Tooltip title={node.node_id}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigator.clipboard.writeText(node.node_id).catch(() => {})}
                          >
                            {node.node_id.substring(0, 8)}...
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={node.state} color={node.state === 'ONLINE' ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        <div>
                          {String(node.os_info?.platform ?? '-')} {String(node.os_info?.architecture ?? '')}
                        </div>
                        {node.pytailor_version && (
                          <div
                            css={css`
                              font-size: 0.75rem;
                              color: #666;
                            `}
                          >
                            pytailor {node.pytailor_version}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.cpu_percent != null
                          ? `${node.metrics.cpu_percent}% / ${node.resources?.cpu_count ?? '?'} cores`
                          : String(node.resources?.cpu_count ?? '-')}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.memory_percent != null
                          ? `${node.metrics.memory_percent}% / ${node.resources?.memory_gb ?? '?'} GB`
                          : node.resources?.memory_gb
                            ? `${node.resources.memory_gb} GB`
                            : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.disk_percent != null
                          ? `${node.metrics.disk_percent}% / ${node.metrics.disk_total_gb} GB`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE'
                          ? '-'
                          : (() => {
                              const runningWorkers = workers.filter((w) => w.status === 'RUNNING');
                              const totalCores = runningWorkers.reduce((sum, w) => sum + (w.ncores || 0), 0);
                              const runningTasks = runningWorkers.reduce((sum, w) => sum + (w.running_tasks ?? 0), 0);
                              return `${runningWorkers.length} (${runningTasks} / ${totalCores} cores running)`;
                            })()}
                      </TableCell>
                      <TableCell>{formatLastSeen(node.last_seen)}</TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE' ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => {
                              setRemovingNodes((prev) => new Set(prev).add(node.id));
                              deleteNodeAgent(node.id).then(
                                () => onMutate(),
                                (error) => {
                                  console.error('Failed to remove node:', error);
                                  setRemovingNodes((prev) => {
                                    const next = new Set(prev);
                                    next.delete(node.id);
                                    return next;
                                  });
                                },
                              );
                            }}
                            disabled={removingNodes.has(node.id)}
                          >
                            {removingNodes.has(node.id) ? 'Removing\u2026' : 'Remove'}
                          </Button>
                        ) : (
                          <Button variant="outlined" size="small" onClick={() => onStartWorker(node.id)}>
                            Start Worker
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {showWorkerSection && (
                      <TableRow>
                        <TableCell colSpan={12} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                margin: 2,
                                padding: 2,
                                backgroundColor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom component="div">
                                Workers
                              </Typography>
                              {(() => {
                                const banners = [
                                  ...(isProvisioning ? [{ key: 'provision', label: 'Starting new worker...' }] : []),
                                  ...inFlightBanners,
                                ];
                                return banners.map((b, i) => {
                                  const isLast = i === banners.length - 1;
                                  return (
                                    <Alert
                                      key={b.key}
                                      severity="info"
                                      icon={<CircularProgress size={16} />}
                                      sx={{ mb: isLast ? (hasWorkers ? 2 : 0) : 1 }}
                                    >
                                      {b.label}
                                    </Alert>
                                  );
                                });
                              })()}
                              {hasWorkers && (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Workdir</TableCell>
                                      <TableCell>Worker ID</TableCell>
                                      <TableCell>PID</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Running Cores</TableCell>
                                      <TableCell>Execution</TableCell>
                                      <TableCell>Package</TableCell>
                                      <TableCell>Python</TableCell>
                                      <TableCell>Projects</TableCell>
                                      <TableCell>Capabilities</TableCell>
                                      <TableCell>Last Seen</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {workers.map((worker) => {
                                      const errorMsg =
                                        worker.start_error || worker.cleanup_error || worker.upgrade_error;
                                      const isFailedStatus =
                                        worker.status === 'START_FAILED' ||
                                        worker.status === 'CLEANUP_FAILED' ||
                                        worker.status === 'UPGRADE_FAILED';
                                      const showInlineError = !!errorMsg && isFailedStatus;
                                      const errorLabel =
                                        worker.status === 'START_FAILED'
                                          ? 'Failed to start'
                                          : worker.status === 'UPGRADE_FAILED'
                                            ? 'Upgrade failed'
                                            : 'Cleanup failed';
                                      const rowKey = worker.instance_id || worker.worker_id || worker.name;
                                      return (
                                        <Fragment key={rowKey}>
                                          <TableRow>
                                            <TableCell>
                                              {worker.name}
                                              <Chip
                                                label={worker.managed !== false ? 'Managed' : 'Standalone'}
                                                size="small"
                                                variant="outlined"
                                                color={worker.managed !== false ? 'primary' : 'default'}
                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                              />
                                            </TableCell>
                                            <TableCell>{worker.workdir_name || '-'}</TableCell>
                                            <TableCell
                                              css={css`
                                                font-family: monospace;
                                                font-size: 0.85rem;
                                              `}
                                            >
                                              {worker.worker_id ? (
                                                <Tooltip title={worker.worker_id}>
                                                  <span
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                      navigator.clipboard.writeText(worker.worker_id!).catch(() => {})
                                                    }
                                                  >
                                                    {worker.worker_id.substring(0, 8)}
                                                    ...
                                                  </span>
                                                </Tooltip>
                                              ) : (
                                                '-'
                                              )}
                                            </TableCell>
                                            <TableCell>{worker.pid != null ? worker.pid : '-'}</TableCell>
                                            <TableCell>
                                              {(() => {
                                                const workerKey = worker.instance_id
                                                  ? `${node.id}:${worker.instance_id}`
                                                  : worker.worker_id;
                                                const displayStatus =
                                                  worker.status === 'RUNNING' &&
                                                  workerKey &&
                                                  retiringWorkers.has(workerKey)
                                                    ? 'RETIRING'
                                                    : worker.status === 'RUNNING' &&
                                                        workerKey &&
                                                        upgradingWorkers.has(workerKey)
                                                      ? 'UPGRADING'
                                                      : worker.status;
                                                return (
                                                  <Tooltip
                                                    title={errorMsg || ''}
                                                    disableHoverListener={!errorMsg || showInlineError}
                                                  >
                                                    <Chip
                                                      label={displayStatus}
                                                      color={
                                                        displayStatus === 'RUNNING'
                                                          ? 'success'
                                                          : displayStatus === 'STARTING' ||
                                                              displayStatus === 'UPGRADING'
                                                            ? 'info'
                                                            : displayStatus === 'CLEANUP_FAILED' ||
                                                                displayStatus === 'START_FAILED' ||
                                                                displayStatus === 'UPGRADE_FAILED'
                                                              ? 'error'
                                                              : displayStatus === 'RETIRING'
                                                                ? 'info'
                                                                : displayStatus === 'MISSING'
                                                                  ? 'warning'
                                                                  : 'default'
                                                      }
                                                      size="small"
                                                    />
                                                  </Tooltip>
                                                );
                                              })()}
                                            </TableCell>
                                            <TableCell>
                                              {worker.running_tasks ?? 0} / {worker.ncores}
                                              {(worker.running_tasks ?? 0) > (worker.ncores ?? 0) && (
                                                <Tooltip title="Scaling down — surplus slots finish their current task, then release.">
                                                  <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{ ml: 0.5 }}
                                                  >
                                                    (draining)
                                                  </Typography>
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {worker.execution_mode === 'v2' ? (
                                                <Tooltip
                                                  title={
                                                    worker.max_tasks_per_process == null
                                                      ? 'v2 execution — recycle budget from the worker config default'
                                                      : worker.max_tasks_per_process === 0
                                                        ? 'v2 execution — processes are never recycled'
                                                        : `v2 execution — each process recycles after ${worker.max_tasks_per_process} task(s)`
                                                  }
                                                >
                                                  <Chip
                                                    label={`v2 · ${worker.max_tasks_per_process ?? 'default'}`}
                                                    color="secondary"
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                </Tooltip>
                                              ) : (
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                  legacy
                                                </Typography>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {worker.environment ? (
                                                <Tooltip
                                                  title={`Environment (pytailor ${worker.installed_version || 'unknown'})`}
                                                >
                                                  <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{ fontFamily: 'monospace' }}
                                                  >
                                                    {worker.environment}
                                                  </Typography>
                                                </Tooltip>
                                              ) : (
                                                <>
                                                  {worker.package_name || '-'}
                                                  {worker.installed_version ? ` ${worker.installed_version}` : ''}
                                                </>
                                              )}
                                            </TableCell>
                                            <TableCell>{worker.venv_python_version || '-'}</TableCell>
                                            <TableCell>
                                              <ProjectFilterCell
                                                projectIds={worker.project_id_filter ?? []}
                                                projectNamesById={projectNamesById}
                                                projectsLoaded={projectsLoaded}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {worker.capabilities?.length ? worker.capabilities.join(', ') : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {worker.last_seen ? formatLastSeen(worker.last_seen) : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {(() => {
                                                const instanceKey = worker.instance_id
                                                  ? `${node.id}:${worker.instance_id}`
                                                  : null;
                                                const workerId = worker.worker_id || null;
                                                return (
                                                  <WorkerActionsMenu
                                                    worker={worker}
                                                    pendingFlags={{
                                                      isRetiring: instanceKey
                                                        ? retiringWorkers.has(instanceKey)
                                                        : workerId
                                                          ? retiringWorkers.has(workerId)
                                                          : false,
                                                      isStopping: instanceKey
                                                        ? stoppingWorkers.has(instanceKey)
                                                        : false,
                                                      isUpgrading:
                                                        worker.status === 'UPGRADING' ||
                                                        (instanceKey ? upgradingWorkers.has(instanceKey) : false),
                                                      isCleaningUp: instanceKey
                                                        ? cleaningUpWorkers.has(instanceKey)
                                                        : false,
                                                      isRemoving: instanceKey
                                                        ? removingWorkers.has(instanceKey)
                                                        : false,
                                                      isDeleting: workerId ? deletingWorkers.has(workerId) : false,
                                                      isRestarting: instanceKey
                                                        ? restartingWorkers.has(instanceKey)
                                                        : false,
                                                      isResizing: workerId ? resizingWorkers.has(workerId) : false,
                                                      isEvicting: workerId ? evictingWorkers.has(workerId) : false,
                                                    }}
                                                    onRetire={
                                                      worker.worker_id
                                                        ? () => {
                                                            const wid = worker.worker_id!;
                                                            setRetireTarget({
                                                              workerId: wid,
                                                              workerName: worker.name,
                                                              packageName: worker.package_name,
                                                              packageVersion:
                                                                worker.installed_version || worker.package_version,
                                                              trackingKey:
                                                                worker.managed !== false && worker.instance_id
                                                                  ? `${node.id}:${worker.instance_id}`
                                                                  : wid,
                                                            });
                                                          }
                                                        : undefined
                                                    }
                                                    onStop={
                                                      worker.instance_id
                                                        ? () =>
                                                            setStopTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onUpgrade={
                                                      worker.instance_id && worker.package_name
                                                        ? () =>
                                                            setUpgradeTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                              currentPackageName: worker.package_name!,
                                                              currentVersion:
                                                                worker.installed_version || worker.package_version,
                                                              workdirName: worker.workdir_name,
                                                            })
                                                        : undefined
                                                    }
                                                    onResize={
                                                      worker.worker_id
                                                        ? () =>
                                                            setResizeTarget({
                                                              workerId: worker.worker_id!,
                                                              workerName: worker.name,
                                                              currentCores: worker.ncores ?? 0,
                                                            })
                                                        : undefined
                                                    }
                                                    onEvict={
                                                      worker.worker_id
                                                        ? () => {
                                                            const running = worker.running_tasks ?? 0;
                                                            const isRetiring = worker.status === 'RETIRING';
                                                            setEvictTarget({
                                                              workerId: worker.worker_id!,
                                                              workerName: worker.name,
                                                              isRetiring,
                                                              evictCount: isRetiring
                                                                ? running
                                                                : Math.max(running - (worker.ncores ?? 0), 0),
                                                            });
                                                          }
                                                        : undefined
                                                    }
                                                    onCleanup={
                                                      worker.instance_id
                                                        ? () =>
                                                            setCleanupTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onRemoveManaged={
                                                      worker.instance_id
                                                        ? () => {
                                                            const iid = worker.instance_id!;
                                                            const key = `${node.id}:${iid}`;
                                                            setRemovingWorkers((prev) => new Set(prev).add(key));
                                                            sendNodeCommand(node.id, 'remove_worker', {
                                                              instance_id: iid,
                                                            }).then(
                                                              () => onMutate(),
                                                              (error) => {
                                                                console.error('Failed to remove worker:', error);
                                                                setRemovingWorkers((prev) => {
                                                                  const next = new Set(prev);
                                                                  next.delete(key);
                                                                  return next;
                                                                });
                                                              },
                                                            );
                                                          }
                                                        : undefined
                                                    }
                                                    onRestart={
                                                      worker.instance_id
                                                        ? () => {
                                                            const iid = worker.instance_id!;
                                                            const key = `${node.id}:${iid}`;
                                                            setRestartingWorkers((prev) => new Set(prev).add(key));
                                                            sendNodeCommand(node.id, 'restart_worker', {
                                                              instance_id: iid,
                                                            }).then(
                                                              () => onMutate(),
                                                              (error) => {
                                                                console.error('Failed to restart worker:', error);
                                                                setRestartingWorkers((prev) => {
                                                                  const next = new Set(prev);
                                                                  next.delete(key);
                                                                  return next;
                                                                });
                                                              },
                                                            );
                                                          }
                                                        : undefined
                                                    }
                                                    onDeleteStandalone={
                                                      worker.worker_id
                                                        ? () => {
                                                            const wid = worker.worker_id!;
                                                            setDeletingWorkers((prev) => new Set(prev).add(wid));
                                                            deleteWorker(wid).then(
                                                              () => onMutate(),
                                                              (error) => {
                                                                console.error('Failed to delete worker:', error);
                                                                setDeletingWorkers((prev) => {
                                                                  const next = new Set(prev);
                                                                  next.delete(wid);
                                                                  return next;
                                                                });
                                                              },
                                                            );
                                                          }
                                                        : undefined
                                                    }
                                                  />
                                                );
                                              })()}
                                            </TableCell>
                                          </TableRow>
                                          {showInlineError && (
                                            <TableRow>
                                              <TableCell
                                                colSpan={13}
                                                sx={{ py: 0.5, borderBottom: 0, backgroundColor: 'transparent' }}
                                              >
                                                <Alert
                                                  severity="error"
                                                  variant="outlined"
                                                  sx={{ mb: 1, alignItems: 'flex-start' }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                                  >
                                                    {errorLabel}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{
                                                      m: 0,
                                                      whiteSpace: 'pre-wrap',
                                                      wordBreak: 'break-word',
                                                      fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                      fontSize: '0.8rem',
                                                    }}
                                                  >
                                                    {errorMsg}
                                                  </Typography>
                                                </Alert>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {cleanupTarget && (
        <CleanupWorkerDialog
          nodeAgentId={cleanupTarget.nodeAgentId}
          instanceId={cleanupTarget.instanceId}
          workerName={cleanupTarget.workerName}
          onClose={() => setCleanupTarget(null)}
          onConfirm={() => {
            setCleaningUpWorkers((prev) =>
              new Set(prev).add(`${cleanupTarget.nodeAgentId}:${cleanupTarget.instanceId}`),
            );
            onMutate();
          }}
        />
      )}
      {stopTarget && (
        <StopWorkerDialog
          nodeAgentId={stopTarget.nodeAgentId}
          instanceId={stopTarget.instanceId}
          workerName={stopTarget.workerName}
          onClose={() => setStopTarget(null)}
          onConfirm={() => {
            setStoppingWorkers((prev) => new Set(prev).add(`${stopTarget.nodeAgentId}:${stopTarget.instanceId}`));
            onMutate();
          }}
        />
      )}
      {retireTarget && (
        <RetireWorkerDialog
          workerId={retireTarget.workerId}
          workerName={retireTarget.workerName}
          packageName={retireTarget.packageName}
          packageVersion={retireTarget.packageVersion}
          onClose={() => setRetireTarget(null)}
          onConfirm={() => {
            retiringTimestamps.current.set(retireTarget.trackingKey, Date.now());
            setRetiringWorkers((prev) => new Set(prev).add(retireTarget.trackingKey));
            onMutate();
          }}
        />
      )}
      {upgradeTarget &&
        (() => {
          const node = nodes.find((n) => n.id === upgradeTarget.nodeAgentId);
          const allWorkers = node?.worker_summaries || [];
          const affected = upgradeTarget.workdirName
            ? allWorkers.filter((ws) => ws.workdir_name === upgradeTarget.workdirName && ws.instance_id)
            : allWorkers.filter((ws) => ws.instance_id === upgradeTarget.instanceId);
          return (
            <UpgradeWorkerDialog
              nodeAgentId={upgradeTarget.nodeAgentId}
              instanceId={upgradeTarget.instanceId}
              workerName={upgradeTarget.workerName}
              currentPackageName={upgradeTarget.currentPackageName}
              currentVersion={upgradeTarget.currentVersion}
              affectedWorkers={affected.map((ws) => ({ name: ws.name, runningTasks: ws.running_tasks ?? 0 }))}
              onClose={() => setUpgradeTarget(null)}
              onConfirm={() => {
                const nodeId = upgradeTarget.nodeAgentId;
                setUpgradingWorkers((prev) => {
                  const next = new Map(prev);
                  for (const ws of affected) {
                    const key = `${nodeId}:${ws.instance_id}`;
                    next.set(key, ws.installed_version || ws.package_version);
                  }
                  return next;
                });
                onMutate();
              }}
            />
          );
        })()}
      {resizeTarget && (
        <ResizeWorkerDialog
          workerId={resizeTarget.workerId}
          workerName={resizeTarget.workerName}
          currentCores={resizeTarget.currentCores}
          onClose={() => setResizeTarget(null)}
          onConfirm={(cores) => {
            setResizingWorkers((prev) => new Map(prev).set(resizeTarget.workerId, cores));
            onMutate();
          }}
        />
      )}
      {evictTarget && (
        <EvictWorkerTasksDialog
          workerId={evictTarget.workerId}
          workerName={evictTarget.workerName}
          evictCount={evictTarget.evictCount}
          isRetiring={evictTarget.isRetiring}
          onClose={() => setEvictTarget(null)}
          onConfirm={() => {
            setEvictingWorkers((prev) => new Set(prev).add(evictTarget.workerId));
            onMutate();
          }}
        />
      )}
    </Fragment>
  );
}
